import { getDb, STORE_PENDING } from './db';

/** Marcado a la espera de subir. `flags` y `formPhotos` son extensiones al contrato:
 *  ambos se calculan/capturan al marcar y no pueden reconstruirse al sincronizar. */
export interface PendingAttendance {
  clientUuid: string;
  assignmentId: string;
  kind: 'ingreso' | 'salida';
  deviceTs: string;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  photoBlob: Blob;
  formResponses: { fieldId: string; value: string }[];
  flags: string[];
  formPhotos: { fieldId: string; blob: Blob }[];
}

/** Lo que se guarda en IndexedDB: el marcado más su historial de reintentos.
 *  `attempts` cuenta SOLO los rechazos definitivos del servidor (4xx/RLS): un fallo de
 *  red no consume intentos y lo reinicia, porque estar sin señal no es culpa del recurso. */
export interface QueuedAttendance extends PendingAttendance {
  attempts: number;
  lastError: string | null;
  status: 'pendiente' | 'fallido';
}

/** Rechazos definitivos consecutivos tras los cuales el marcado pasa a `fallido`. */
export const MAX_ATTEMPTS = 5;

/** Puerto de envío. Lo implementa `api/attendance.ts` (Task 26), que se registra a sí
 *  mismo al importarse; así la cola no depende de un módulo posterior. */
export interface AttendanceUploader {
  uploadAttendancePhoto(assignmentId: string, clientUuid: string, photo: Blob): Promise<string>;
  uploadFormFieldPhoto(clientUuid: string, fieldId: string, photo: Blob): Promise<string>;
  submitAttendance(input: {
    clientUuid: string;
    assignmentId: string;
    kind: 'ingreso' | 'salida';
    deviceTs: string;
    lat: number | null;
    lng: number | null;
    accuracy: number | null;
    photoPath: string;
    origin: 'online' | 'offline_sync';
    flags: string[];
    formResponses: { fieldId: string; value: string }[];
  }): Promise<{ id: string }>;
  isDuplicateError(err: unknown): boolean;
  /** true si el servidor rechazó el marcado de forma DEFINITIVA (4xx/RLS distinto de
   *  23505). Un fallo de red o un 5xx devuelven false: eso se reintenta siempre. */
  isPermanentError(err: unknown): boolean;
}

let uploader: AttendanceUploader | null = null;

/** Registra el enviador real (lo llama `api/attendance.ts` al importarse). */
export function setAttendanceUploader(u: AttendanceUploader): void {
  uploader = u;
}

/** Guarda un marcado en la cola local. Idempotente por `clientUuid`: si ya estaba,
 *  conserva su historial de reintentos y solo actualiza los datos del marcado. */
export async function enqueue(p: PendingAttendance): Promise<void> {
  const db = await getDb();
  const existing = await db.get(STORE_PENDING, p.clientUuid);
  await db.put(STORE_PENDING, {
    ...p,
    attempts: existing?.attempts ?? 0,
    lastError: existing?.lastError ?? null,
    status: existing?.status ?? 'pendiente',
  });
}

/** Marcados que todavía esperan subir (los `fallido` NO cuentan: ya no se reintentan
 *  y el recurso los ve en su propia sección). Los usa MiTurno para el estado del turno. */
export async function listPending(): Promise<QueuedAttendance[]> {
  const db = await getDb();
  const all = await db.getAll(STORE_PENDING);
  return all.filter((i) => i.status !== 'fallido');
}

/** Marcados que el servidor rechazó de forma definitiva tras `MAX_ATTEMPTS` intentos. */
export async function listFailed(): Promise<QueuedAttendance[]> {
  const db = await getDb();
  const all = await db.getAll(STORE_PENDING);
  return all.filter((i) => i.status === 'fallido');
}

/** Cuántos marcados esperan subir (sin contar los fallidos). */
export async function pendingCount(): Promise<number> {
  return (await listPending()).length;
}

/** Borra un marcado de la cola local. Lo usa el botón "Descartar" de un marcado fallido. */
export async function discardPending(clientUuid: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_PENDING, clientUuid);
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Sube todos los pendientes: foto a Storage + insert de asistencia con
 *  `origin: 'offline_sync'`. Idempotente por `clientUuid`: si el servidor responde
 *  duplicado, el ítem se considera enviado y se borra. Un fallo de red deja el ítem
 *  en la cola y NO interrumpe al resto.
 *
 *  Rechazos definitivos (RLS 42501 porque la asignación fue rechazada o reemplazada
 *  mientras el recurso estaba sin señal, 4xx del Storage, …) no pueden reintentarse
 *  para siempre: se cuentan en `attempts` y a los `MAX_ATTEMPTS` el marcado pasa a
 *  `fallido`, sale de los pendientes (se apaga el banner eterno) y queda visible para
 *  el recurso con su motivo y la opción de descartarlo. */
export async function flush(): Promise<{ sent: number; remaining: number }> {
  const db = await getDb();
  const items = (await db.getAll(STORE_PENDING)).filter((i) => i.status !== 'fallido');
  if (items.length === 0) return { sent: 0, remaining: 0 };
  if (!uploader) {
    throw new Error('La cola offline no tiene enviador registrado (importa api/attendance).');
  }

  let sent = 0;
  for (const item of items) {
    try {
      const photoPath = await uploader.uploadAttendancePhoto(
        item.assignmentId,
        item.clientUuid,
        item.photoBlob,
      );
      const extra: { fieldId: string; value: string }[] = [];
      for (const fp of item.formPhotos) {
        const path = await uploader.uploadFormFieldPhoto(item.clientUuid, fp.fieldId, fp.blob);
        extra.push({ fieldId: fp.fieldId, value: path });
      }
      await uploader.submitAttendance({
        clientUuid: item.clientUuid,
        assignmentId: item.assignmentId,
        kind: item.kind,
        deviceTs: item.deviceTs,
        lat: item.lat,
        lng: item.lng,
        accuracy: item.accuracy,
        photoPath,
        origin: 'offline_sync',
        flags: item.flags,
        formResponses: [...item.formResponses, ...extra],
      });
      await db.delete(STORE_PENDING, item.clientUuid);
      sent += 1;
    } catch (err) {
      if (uploader.isDuplicateError(err)) {
        await db.delete(STORE_PENDING, item.clientUuid);
        sent += 1;
        continue;
      }
      // Rechazo definitivo: suma un intento. Fallo de red o 5xx: reintento infinito
      // (y el contador vuelve a 0, porque el servidor nunca llegó a opinar).
      const permanent = uploader.isPermanentError(err);
      const attempts = permanent ? item.attempts + 1 : 0;
      await db.put(STORE_PENDING, {
        ...item,
        attempts,
        lastError: errorMessage(err),
        status: permanent && attempts >= MAX_ATTEMPTS ? 'fallido' : 'pendiente',
      });
    }
  }

  const remaining = (await listPending()).length;
  return { sent, remaining };
}
